import { useEffect, useRef, useState } from 'react';

interface Props {
    org: string;
    repo: string;
    environmentID: string;
    value: string;
    onChange: (branch: string) => void;
}

export default function BranchSelector({ org, repo, environmentID, value, onChange }: Props) {
    const [search, setSearch] = useState(value);
    const [options, setOptions] = useState<string[]>([]);
    const [highlight, setHighlight] = useState(-1);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const abortRef = useRef<AbortController | null>(null);
    const listRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setSearch(value);
    }, [value]);

    useEffect(() => {
        if (highlight >= 0 && listRef.current) {
            const el = listRef.current.children[highlight] as HTMLElement | undefined;
            el?.scrollIntoView({ block: 'nearest' });
        }
    }, [highlight]);

    const clear = () => {
        abortRef.current?.abort();
        setOptions([]);
        setHighlight(-1);
        setOpen(false);
        setLoading(false);
    };

    const updateSearch = async (q: string) => {
        abortRef.current?.abort();
        setSearch(q);
        if (!q) {
            clear();
            return;
        }
        const controller = new AbortController();
        abortRef.current = controller;
        setLoading(true);
        setOpen(true);
		try {
			const resp = await fetch(
				`/api/repos/${encodeURIComponent(org)}/${encodeURIComponent(repo)}/branches?environmentID=${encodeURIComponent(environmentID)}&q=${encodeURIComponent(q)}`,
				{ signal: controller.signal },
			);
            if (resp.ok) {
                const data: string[] = await resp.json();
                setOptions(data);
                setHighlight(data.length > 0 ? 0 : -1);
            } else {
                setOptions([]);
            }
        } catch (err) {
            if ((err as any).name !== 'AbortError') {
                console.error('search branches error', err);
            }
        } finally {
            if (!controller.signal.aborted) {
                setLoading(false);
            }
        }
    };

    const choose = (b: string) => {
        onChange(b);
        setSearch(b);
        clear();
    };

    const pageJump = (dir: 1 | -1) => {
        if (!listRef.current) {
            setHighlight(dir === 1 ? options.length - 1 : 0);
            return;
        }
        const container = listRef.current;
        const item = container.firstElementChild as HTMLElement | null;
        const itemHeight = item?.offsetHeight || 1;
        const visibleCount = Math.floor(container.clientHeight / itemHeight) || options.length;
        setHighlight((h) => {
            if (options.length <= visibleCount) {
                return dir === 1 ? options.length - 1 : 0;
            }
            if (h < 0) {
                return dir === 1 ? visibleCount : 0;
            }
            if (dir === 1) {
                const next = Math.floor(h / visibleCount) * visibleCount + visibleCount;
                return Math.min(next, options.length - 1);
            } else {
                const next = Math.floor(h / visibleCount) * visibleCount - 1;
                return Math.max(next, 0);
            }
        });
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((h) => Math.min(h + 1, options.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
        } else if (e.key === 'PageDown') {
            e.preventDefault();
            pageJump(1);
        } else if (e.key === 'PageUp') {
            e.preventDefault();
            pageJump(-1);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (highlight >= 0 && options[highlight]) {
                choose(options[highlight]);
            }
        } else if (e.key === 'Escape') {
            e.preventDefault();
            clear();
        }
    };

    return (
        <div style={{ position: 'relative' }}>
            <input
                value={search}
                onChange={(e) => updateSearch(e.target.value)}
                onKeyDown={handleKeyDown}
                style={{ width: '100%', padding: '6px', boxSizing: 'border-box' }}
            />
            {open && (
                options.length > 0 ? (
                    <div
                        ref={listRef}
                        style={{
                            border: '1px solid #ccc',
                            position: 'absolute',
                            background: '#fff',
                            zIndex: 1,
                            maxHeight: '200px',
                            overflowY: 'auto',
                            width: '100%',
                        }}
                    >
                        {options.map((b, idx) => (
                            <div
                                key={b}
                                style={{ padding: '4px', cursor: 'pointer', background: idx === highlight ? '#def' : undefined }}
                                onMouseEnter={() => setHighlight(idx)}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => choose(b)}
                            >
                                {b}
                            </div>
                        ))}
                    </div>
                ) : (
                   !loading && (
                       <div
                           style={{
                               border: '1px solid #ccc',
                               position: 'absolute',
                               background: '#fff',
                               zIndex: 1,
                               padding: '4px',
                               width: '100%',
                           }}
                        >
                            No results
                        </div>
                    )
                )
            )}
        </div>
    );
}
